import { FormEvent, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useHistory } from 'react-router-dom';
import { CouncilRegion } from 'shared-components';
import { AddFoodLicensePayload } from '../../Services/InsuranceLicenseService';
import { ErrorHandler } from '../../Services/UserApi';
import FoodLicenseForm from './FoodLicenseForm';
import { FoodLicenseFormState, INITIAL_FOOD_FORM } from './InsuranceLicenseTypes';
import {
	listFormErrors,
	MAX_EVIDENCE_FILES,
	toUnixDateString,
	validateEvidenceFile,
	validateEvidenceFiles,
	validateFoodLicenseForm,
} from './InsuranceLicenseUtils';

type Props = {
	councilRegions: CouncilRegion[];
	addFoodLicense: (payload: AddFoodLicensePayload) => Promise<unknown>;
};

export default function FoodLicenseAddSection(props: Props) {
	const { councilRegions, addFoodLicense } = props;
	const history = useHistory();

	const [form, setForm] = useState(INITIAL_FOOD_FORM);
	const [evidenceFiles, setEvidenceFiles] = useState<File[]>([]);
	const [inlineEvidenceError, setInlineEvidenceError] = useState<string | undefined>();
	const [isSaving, setIsSaving] = useState(false);
	const [submitAttempted, setSubmitAttempted] = useState(false);
	const [hasInteracted, setHasInteracted] = useState(false);

	const errors = useMemo(() => validateFoodLicenseForm(form), [form]);
	const errorList = useMemo(() => listFormErrors(errors), [errors]);
	const evidenceValidationError = useMemo(() => validateEvidenceFiles(evidenceFiles), [evidenceFiles]);
	const evidenceError =
		inlineEvidenceError ?? (submitAttempted || hasInteracted ? evidenceValidationError : undefined);
	const combinedErrorList = useMemo(
		() => [...errorList, ...(evidenceValidationError ? [evidenceValidationError] : [])],
		[errorList, evidenceValidationError],
	);
	const isSubmitDisabled = (hasInteracted || submitAttempted) && combinedErrorList.length > 0;

	const onFormChange = (nextForm: FoodLicenseFormState) => {
		setHasInteracted(true);
		setForm(nextForm);
	};

	const onAddEvidenceFiles = (fileList: FileList | null) => {
		if (!fileList) {
			return;
		}

		let firstInlineError: string | undefined;
		const nextFiles = [...evidenceFiles];
		for (const file of Array.from(fileList)) {
			if (nextFiles.length >= MAX_EVIDENCE_FILES) {
				firstInlineError = `You can upload up to ${MAX_EVIDENCE_FILES} files`;
				break;
			}
			const fileError = validateEvidenceFile(file);
			if (fileError) {
				firstInlineError = fileError;
				continue;
			}
			nextFiles.push(file);
		}

		setInlineEvidenceError(firstInlineError);
		setEvidenceFiles(nextFiles);
		setHasInteracted(true);
	};

	const onRemoveEvidenceFile = (index: number) => {
		setEvidenceFiles((prev) => prev.filter((_, fileIndex) => fileIndex !== index));
		setInlineEvidenceError(undefined);
		setHasInteracted(true);
	};

	const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		setSubmitAttempted(true);
		if (combinedErrorList.length > 0) {
			toast.error(combinedErrorList[0] ?? 'Please check your food license details');
			return;
		}

		setIsSaving(true);
		try {
			await addFoodLicense({
				CouncilRegionId: Number(form.councilRegionId),
				FoodLicenseNumber: form.licenseNumber.trim(),
				FoodLicenseType: form.licenseType,
				FoodLicenseExpiry: toUnixDateString(form.expiryDate),
				files: evidenceFiles,
			});
			setForm(INITIAL_FOOD_FORM);
			setEvidenceFiles([]);
			setInlineEvidenceError(undefined);
			setHasInteracted(false);
			setSubmitAttempted(false);
			toast.success('Food license added');
			history.push('/');
		} catch (error) {
			ErrorHandler.toast(error);
		} finally {
			setIsSaving(false);
		}
	};

	return (
		<section className="insurance-license-section insurance-license-section--add">
			<FoodLicenseForm
				form={form}
				errors={errors}
				evidenceFiles={evidenceFiles}
				evidenceError={evidenceError}
				isSaving={isSaving}
				councilRegions={councilRegions}
				onSubmit={onSubmit}
				onChange={onFormChange}
				onAddEvidenceFiles={onAddEvidenceFiles}
				onRemoveEvidenceFile={onRemoveEvidenceFile}
				showAllErrors={submitAttempted || Boolean(inlineEvidenceError)}
				isSubmitDisabled={isSubmitDisabled}
			/>
		</section>
	);
}
